import { Download } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { buttonVariants } from 'components/ui/button';
import { useIsMobile } from 'hooks/use-mobile';
import { useGlobalQuery } from 'state/query-client/hooks';

type DesktopDownload = { url: string; name: string; size: number };

export type DesktopRelease = {
  version: string;
  mac?: { arm64?: DesktopDownload; x64?: DesktopDownload };
};

// Written by CI (.github/workflows/desktop-build.yml) and served by this web app. Each
// environment has its own folder, e.g. /desktop/production/, so merging dev → stg → main
// never offers one environment's build on another.
const fetchLatestRelease = async (channel: string): Promise<DesktopRelease | null> => {
  const response = await fetch(`/desktop/${channel}/latest.json`, { cache: 'no-store' });
  if (!response.ok) return null;
  return response.json();
};

export function DesktopAppDownload() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const channel = import.meta.env.VITE_APP_ENV ?? '';
  // The preload script sets this inside the desktop app, which needs no download link.
  const isDesktopApp = 'klaxDesktop' in window;

  const { data: release } = useGlobalQuery({
    queryKey: ['desktop-release', channel],
    queryFn: () => fetchLatestRelease(channel),
    enabled: !isDesktopApp && Boolean(channel),
    retry: false,
  });

  const mac = release?.mac;
  if (isMobile || isDesktopApp || !mac?.arm64 || !mac?.x64) return null;

  const linkClassName = buttonVariants({ variant: 'outline', size: 'sm' });

  return (
    <div className="flex flex-col items-center gap-2">
      <p className="flex items-center gap-2 text-sm text-grey-500">
        <Download aria-hidden="true" className="h-4 w-4" />
        {t('DOWNLOAD_KLAX_FOR_MAC', 'Download KLAX for Mac')}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <a href={mac.arm64.url} download className={linkClassName}>
          {t('MAC_APPLE_SILICON', 'Apple Silicon')}
        </a>
        <a href={mac.x64.url} download className={linkClassName}>
          {t('MAC_INTEL', 'Intel')}
        </a>
      </div>
    </div>
  );
}
